import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { MeetService } from '@base/services/private/meet.service';
import { IdParams } from '@base/models/requests/requests.req';
import { RateProfile } from '@base/models/responses/rate-profile.res';
import { SkeletonGeneralListComponent } from '@base/components/private/skeleton/skeleton-general-list/skeleton-general-list.component';
import { SaveRateMeetingParams } from '@base/models/requests/rate-rating.req';
import { FormsModule } from '@angular/forms';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { ImgErrorFallbackDirective } from '@base/directives/img-error-fallback.directive';
import { defaultUserImg } from '@base/constants/common';
import { GlobalService } from '@base/services/private/global.service';
import { Subscription } from 'rxjs';
import { StatusResponse } from '@base/enums/status-response.enum';
import { RateMeetingSource } from '@base/enums/rate-meeting.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { TypeOfCta } from '@base/enums/schedule.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { MeetingStatus } from '@base/enums/meet.enum';

@Component({
  selector: 'app-modal-rate-meeting',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    SkeletonGeneralListComponent,
    TranslatePipe,
    FormsModule,
    ImgErrorFallbackDirective,
    ModalFooterComponent,
    forwardRef(() => TranslatePipe),
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-rate-meeting.component.html',
  styleUrl: './modal-rate-meeting.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalRateMeetingComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly translatePipe = inject(TranslatePipe);

  @Input() peopleId = '';
  @Input() modalId = '';
  @Input() isShowCloseBtn = true;
  @Input() source: RateMeetingSource = RateMeetingSource.PEOPLE_CTA;
  @Input() isLastRated = false;
  @Input() action: TypeOfRequestLabel | string = '';

  rateProfile: RateProfile | null = null;
  get howWasQuestionKey(): string {
    return this.rateProfile?.is_unstructured ? 'HOW_WAS_THE_CHAT' : 'HOW_WAS_THE_MEETING';
  }
  get didNotAttendKey(): string {
    return this.rateProfile?.is_unstructured ? 'PERSON_HASNT_RESPONDED' : 'PERSON_DID_NOT_ATTEND';
  }
  rateValue = 0;
  feedbackPlaceholder = '';
  feedback = '';
  errorMessage = '';
  defaultUserImg = defaultUserImg;

  isHttpRequestRunning = false;
  isShowPlaceholder = true;

  RateMeetingSource = RateMeetingSource;

  httpRequestObserver$: Subscription | null = null;

  rating = this.globalStateSrv.getState().loader?.rating;

  ngOnInit(): void {
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  ngAfterViewInit(): void {
    this.getRateProfile();
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Fetches the rate profile for a meeting using the MeetService and updates the component state.
   * @returns void
   */
  getRateProfile(): void {
    // Logic to get the rate profile goes here
    const params: IdParams = { id: this.peopleId };
    this.meetSrv.postGetRateProfileMeeting(params).subscribe({
      next: (res) => {
        this.rateProfile = res;

        this.feedback = this.rateProfile.feedback;
        this.rateValue = this.rateProfile.rate_value;
        this.onChangePlaceholder();

        setTimeout(() => {
          this.isShowPlaceholder = false;
          this.triggerChangeDetection();
        }, 300);
      },
      error: (err) => {
        console.error('Error fetching rate profile:', err);
        this.isShowPlaceholder = false;
      },
    });
  }

  /**
   * Closes the modal by calling the ModalService with the provided modalId.
   * @returns void
   */
  close(): void {
    if (this.rating?.is_rate_skippable) {
      // if during event, close all modal
      this.modalSrv.closeAll();
      this.modalSrv.close();
      return;
    }
    this.modalSrv.close(this.modalId);
  }

  /**
   * Submits the rating for the meeting.
   * @returns void
   */
  submit(): void {
    const data: SaveRateMeetingParams = {
      id_attendee: this.peopleId,
      value: this.rateValue,
      feedback: this.feedback,
    };

    this.save(data);
  }

  /**
   * Saves the rating data for a meeting when the person did not attend.
   * @returns void
   */
  saveWithPeopleDidNotAttend(): void {
    this.feedback = '';
    const data: SaveRateMeetingParams = {
      id_attendee: this.peopleId,
      value: 0,
      feedback: '',
    };

    this.save(data);
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Saves the rating data for a meeting.
   * @param data - The rating data to save.
   * @returns void
   */
  save(data: SaveRateMeetingParams): void {
    this.meetSrv.postSaveRateMeeting(data).subscribe({
      next: (res) => {
        if (res.status === StatusResponse.SUCCESS) {
          this.modalSrv.close(this.modalId, { key: this.source });
          this.errorMessage = '';
          (async () => {
            this.doUpdateState();
          })();
        } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
          // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
          this.showModalWarning();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage = err.message || this.translatePipe.transform('SOMETHING_WENT_WRONG');
        console.error('Error saving rate meeting:', err);
      },
    });
  }

  /**
   * Handles the click event on a star for rating.
   * @param index - The index of the clicked star (1-5).
   * @returns void
   */
  onClickStar(index: number): void {
    const value = index;
    this.rateValue = value;
    this.onChangePlaceholder();
  }

  /**
   * Selects the feedback prompt for the score and the chat or meeting type.
   * @returns void
   */
  onChangePlaceholder(): void {
    switch (this.rateValue) {
      case 1:
        this.feedbackPlaceholder = 'IF_THE_PERSON_DID_NOT_SHOW_UP';
        break;
      case 2:
        this.feedbackPlaceholder = 'WHAT_DID_NOT_GO_SO_WELL';
        break;
      case 3:
        this.feedbackPlaceholder = this.rateProfile?.is_unstructured
          ? 'HOW_CAN_THE_CHAT_BE_IMPROVED'
          : 'HOW_CAN_THE_MEETING_BE_IMPROVED';
        break;
      case 4:
        this.feedbackPlaceholder = this.rateProfile?.is_unstructured
          ? 'WHAT_PREVENTED_THE_CHAT_FROM_REACHING_A_PERFECT_SCORE'
          : 'WHAT_PREVENTED_THE_MEETING_FROM_REACHING_A_PERFECT_SCORE';
        break;
      case 5:
        this.feedbackPlaceholder = this.rateProfile?.is_unstructured
          ? 'WHAT_MADE_THE_CHAT_EXCEPTIONAL'
          : 'WHAT_MADE_THE_MEETING_EXCEPTIONAL';
        break;
      default:
        break;
    }
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Gets the meeting status and updates the request label service.
   * @returns void
   */
  async doUpdateState(): Promise<void> {
    const data = {
      status: true,
      data: {
        id: this.peopleId,
        action: this.action,
        newValue: {
          available_cta: [TypeOfCta.RATE],
          meeting_status: MeetingStatus.RATED_PAST_MEETING,
          isRatedPastMeeting: true, // additional indicator to change the UI
        },
      },
    };
    this.requestLabelSrv.setEventUpdated(data);
  }
}
